import { type AlertNotificationItem } from './AlertNotification';

export type AlertTransitionDevice = {
  connection_status: string;
  device_id: string;
  device_name?: string;
  program_status: string;
};

export type AlertTransitionSnapshot = {
  connection_status: string;
  device_id: string;
  device_name: string;
  program_status: string;
};

export type AlertTransitionResult = {
  alerts: AlertNotificationItem[];
  nextState: Record<string, AlertTransitionSnapshot>;
};

const CONNECTION_DISCONNECTED = 'DISCONNECT';

const PROGRAM_STOPPED = 'STOPPED';
const PROGRAM_UNKNOWN = 'UNKNOWN';

const normalizeStatus = (status: string) =>
  status.trim().toUpperCase();

const isConnectionAbnormal = (status: string) =>
  normalizeStatus(status) === CONNECTION_DISCONNECTED;

const isProgramAbnormal = (status: string) => {
  const normalizedStatus = normalizeStatus(status);

  return (
    normalizedStatus === PROGRAM_STOPPED ||
    normalizedStatus === PROGRAM_UNKNOWN
  );
};

const normalizeDevice = (
  device: AlertTransitionDevice
): AlertTransitionSnapshot => ({
  connection_status: normalizeStatus(
    device.connection_status
  ),
  device_id: device.device_id,
  device_name:
    device.device_name?.trim() ||
    device.device_id,
  program_status: normalizeStatus(
    device.program_status
  )
});

const buildStateMap = (
  devices: AlertTransitionDevice[]
): Record<string, AlertTransitionSnapshot> =>
  devices.reduce<
    Record<string, AlertTransitionSnapshot>
  >((result, device) => {
    const normalizedDevice =
      normalizeDevice(device);

    result[normalizedDevice.device_id] =
      normalizedDevice;

    return result;
  }, {});

const createAlertId = (
  deviceId: string,
  alertIndex: number
) =>
  `device-alert-${Date.now()}-${alertIndex}-${deviceId}`;

/*
 * Compare the previous Device Monitor state with the latest state
 * and generate Alert Notification events only for newly occurring
 * abnormalities.
 *
 * Important behavior:
 *
 * - The first Device Monitor fetch generates no notifications.
 * - A device that appears for the first time generates no notification.
 * - Recovery changes generate no notification.
 * - A device generates at most one notification per polling cycle.
 * - Connection-only abnormalities are not notified.
 * - If Connection and Program become abnormal in the same polling
 *   cycle, they are combined into one notification.
 */
export const evaluateAlertTransitions = (
  previousState:
    | Record<string, AlertTransitionSnapshot>
    | null,
  currentDevices: AlertTransitionDevice[]
): AlertTransitionResult => {
  const nextState = buildStateMap(currentDevices);

  /*
   * Initial fetch:
   * Store the current state as the comparison baseline only.
   */
  if (!previousState) {
    return {
      alerts: [],
      nextState
    };
  }

  const alerts: AlertNotificationItem[] = [];

  Object.values(nextState).forEach(currentDevice => {
    const previousDevice =
      previousState[currentDevice.device_id];

    /*
     * A Device that has not previously been observed becomes a new
     * baseline. Do not treat its existing state as a new abnormality.
     */
    if (!previousDevice) {
      return;
    }

    const connectionWasAbnormal =
      isConnectionAbnormal(
        previousDevice.connection_status
      );

    const connectionIsAbnormal =
      isConnectionAbnormal(
        currentDevice.connection_status
      );

    const programWasAbnormal =
      isProgramAbnormal(
        previousDevice.program_status
      );

    const programIsAbnormal =
      isProgramAbnormal(
        currentDevice.program_status
      );

    const connectionBecameAbnormal =
      !connectionWasAbnormal &&
      connectionIsAbnormal;

    const programBecameAbnormal =
      !programWasAbnormal &&
      programIsAbnormal;

    /*
     * Nothing newly became abnormal.
     *
     * This also excludes:
     * - unchanged abnormal states
     * - recovery changes
     * - STOPPED <-> UNKNOWN while Program was already abnormal
     */
    if (
      !connectionBecameAbnormal &&
      !programBecameAbnormal
    ) {
      return;
    }

    /*
     * Connection-only alerts are not part of the Alert Notification
     * specification. A notification is emitted only when Program is
     * currently STOPPED or UNKNOWN.
     */
    if (!programIsAbnormal) {
      return;
    }

    const alert: AlertNotificationItem = {
      deviceName: currentDevice.device_name,
      id: createAlertId(
        currentDevice.device_id,
        alerts.length
      ),
      programStatus:
        currentDevice.program_status
    };

    /*
     * Whenever the current Device state is disconnected, include
     * Connection in the snapshot shown by the notification.
     *
     * This covers both:
     *
     * CONNECT / SUCCESS
     *   -> DISCONNECT / UNKNOWN
     *
     * and:
     *
     * CONNECT / STOPPED
     *   -> DISCONNECT / UNKNOWN
     */
    if (connectionIsAbnormal) {
      alert.connectionStatus =
        currentDevice.connection_status;
    }

    alerts.push(alert);
  });

  return {
    alerts,
    nextState
  };
};